import { faStar as faStarRegular, faStar as faStarSolid } from "@fortawesome/pro-duotone-svg-icons";
import { batch } from "@preact/signals-react";
import { Button } from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { useEffect, useState } from "react";
import { ulid } from "ulid";
import {
  branchDataset,
  companyDataset,
  departmentDataset,
  isTemplateFavorite,
  toggleFavoriteTemplate,
  userData,
} from "../Common/Store/CommonSignals";
import { ColorChangingIcon } from "../Common/UI/ColorChangingIcon";
import EntryPage from "../Entry/Manage/EditEntry";
import {
  disabledSections,
  entryId,
  entryValues,
  formErrors,
  galleryValues,
  initialCommentsLengths,
  isEntryPreview,
  selectedStep,
  selectedTab,
  steps,
  template,
  templateId,
  templateStageSteps,
} from "../Entry/Manage/EditEntrySignals";
import SyncData from "../Utilities/DataSync/SyncData";
import {
  getConnectionData,
  getEntryValues,
  setInitialGalleryValues,
  validateStageStep,
} from "../Utilities/EntryUtils";
import {
  isStageEditor,
  isTemplatePersisted,
  showTemplateHistoryWindow,
  showTemplatePreviewWindow,
  templateData,
  templateHasUnsavedChanges,
} from "./NewTemplateSignals";
import SectionPanelBar from "./SectionPanelBar";

export const TemplateForm = () => {
  // Adds a new section to the template

  isStageEditor.value = false;

  function addSection() {
    const newSection = {
      sectionId: ulid(),
      sectionName: "New Section",
      fields: [
        {
          fieldId: ulid(),
          fieldName: "",
          fieldType: "",
          properties: {},
        },
      ],
      properties: {},
    };
    templateData.value = {
      ...templateData.value,
      sections: [...templateData.value.sections, newSection],
    };
  }

  const getRevisionCount = (obj, fieldName) => {
    for (const section of obj.sections) {
      for (const field of section.fields) {
        if (field.fieldName === fieldName) {
          return field.revisionCount ?? 0;
        }
      }
    }
    return "Rev # Not Found";
  };

  const [previewVisible, setPreviewVisible] = useState(false);

  const togglePreview = () => {
    batch(() => {
      template.value = templateData.value;
      templateId.value = template.value.id ? template.value.id : 1;
      isEntryPreview.value = true;
      galleryValues.value = [];
      const updatedSections = template.value.sections.map((section, sIndex) => ({
        ...section,
        properties: section.properties || {},
        fields: section.fields.map((field, fIndex) => {
          const fieldType = field.fieldType;
          let fieldValue;
          if (fieldType === "BusinessUnit") {
            fieldValue = userData.value?.officeLocation;
          } else if (fieldType === "Checkbox") {
            fieldValue = "false";
          } else if (
            typeof field.properties?.DefaultValue === "boolean" &&
            fieldType.includes("Date")
          ) {
            const newDate = new Date();
            if (field.properties?.HoursOnly) {
              newDate.setMinutes(0);
              newDate.setSeconds(0);
              newDate.setMilliseconds(0);
            }
            const timezoneOffset = new Date().getTimezoneOffset() * 60000;
            const convertedDate = new Date(newDate.getTime() - timezoneOffset)
              .toISOString()
              .replace("Z", "");
            fieldValue = field.properties?.DefaultValue ? convertedDate : "";
          } else {
            fieldValue = field.properties?.DefaultValue ?? "";
          }
          if (fieldType === "Gallery") {
            const initialGalleryValues = setInitialGalleryValues({
              galleryName: field.fieldName,
              values: getConnectionData({
                field,
                Data: field.properties?.Data,
              }),
              fields: field.properties?.Fields,
              field: field,
            });

            entryValues.value = {
              ...entryValues.value,
              [field.fieldName]: initialGalleryValues,
            };

            const newTemplateView = JSON.tryParse(JSON.stringify(template.value)); // Deep copy
            let newField = {
              ...newTemplateView.sections[sIndex].fields[fIndex],
              Values: initialGalleryValues,
            };
            newTemplateView.sections[sIndex].fields[fIndex] = newField;

            template.value = newTemplateView;

            return {
              ...field,
              value: "",
              properties: {
                ...field.properties,
                Values: initialGalleryValues,
              },
            };
          }
          if (field.properties?.["RevisionCount"]) {
            return {
              ...field,
              value: fieldValue,
              revisionCount: getRevisionCount({ ...template.value }, field.fieldName),
            };
          }
          return {
            ...field,
            value: fieldValue,
          };
        }),
      }));
      template.value = { ...template.value, sections: updatedSections };
      entryValues.value = getEntryValues(template.value);
      entryId.value = null;
      formErrors.value = {};
      disabledSections.value = {};
      selectedTab.value = 0;
      selectedStep.value = 0;
      initialCommentsLengths.value = {};
      steps.value = templateStageSteps.value?.map((step) => ({ label: step.text })) ?? [];
    });
    validateStageStep();

    showTemplatePreviewWindow.value = !previewVisible;
    setPreviewVisible(!previewVisible);
  };

  const [initialTemplateValues, setInitialTemplateValues] = useState({});

  useEffect(() => {
    if (Object.keys(initialTemplateValues).length === 0) {
      const { type, label, permission, ...templateDataInitial } = templateData.value;
      setInitialTemplateValues({ ...templateDataInitial });
    }
  }, [templateData.value]);

  useEffect(() => {
    const hasUnsavedChanges = detectUnsavedChanges();
    if (hasUnsavedChanges !== templateHasUnsavedChanges.value) {
      templateHasUnsavedChanges.value = hasUnsavedChanges;
    }
  }, [templateData.value, initialTemplateValues]);

  const detectUnsavedChanges = () => {
    const currentValues = templateData.value;
    for (const key in initialTemplateValues) {
      if (!deepEqual(initialTemplateValues[key], currentValues[key])) {
        return true;
      }
    }
    return false;
  };

  const deepEqual = (initialValues, currentValues) => {
    if (
      Array.isArray(initialValues) &&
      Array.isArray(currentValues) &&
      initialValues.length === 0 &&
      currentValues.length === 0
    ) {
      return true;
    }
    if ((Array.isArray(initialValues) && initialValues.length === 0) || initialValues === "[]") {
      return currentValues === "" || currentValues === "[]";
    }
    if ((Array.isArray(currentValues) && currentValues.length === 0) || currentValues === "[]") {
      return initialValues === "" || initialValues === "[]";
    }
    return JSON.stringify(initialValues) === JSON.stringify(currentValues);
  };

  return (
    <div className="k-p-4">
      <div className="k-d-flex k-align-items-center k-gap-2">
        <Label style={{ fontSize: "24px" }}>
          <strong>
            {isTemplatePersisted.value ? (
              "Edit " + templateData.value.templateName
            ) : (
              <div>Create New Template</div>
            )}
          </strong>
        </Label>
        {isTemplatePersisted.value && (
          <ColorChangingIcon
            key={`star-${templateData.value.id}-${isTemplateFavorite(templateData.value.id)}`}
            icon={isTemplateFavorite(templateData.value.id) ? faStarSolid : faStarRegular}
            initialPrimaryColor={isTemplateFavorite(templateData.value.id) ? "#FFA500" : "#999999"}
            initialSecondaryColor={
              isTemplateFavorite(templateData.value.id) ? "#FFD700" : "#979797ff"
            }
            initialSize={24}
            hoverSize={24}
            tooltip={
              isTemplateFavorite(templateData.value.id)
                ? "Remove from favorites"
                : "Add to favorites"
            }
            onClick={(e) => {
              e.stopPropagation();
              toggleFavoriteTemplate(templateData.value.id);
            }}
          />
        )}
        <Button
          type="button"
          onClick={togglePreview}
          className="!k-ml-4"
        >
          Show Preview
        </Button>

        {isTemplatePersisted.value && (
          <Button
            type="button"
            onClick={() => {
              showTemplateHistoryWindow.value = true;
            }}
            className="!k-ml-4"
          >
            Show History
          </Button>
        )}

        {templateHasUnsavedChanges.value && (
          <Label
            className={"k-pl-4 k-pr-4"}
            editorValid={false}
          >
            There are unsaved changes in the template
          </Label>
        )}
      </div>

      {previewVisible && (
        <Window
          title={"Template Preview"}
          onClose={togglePreview}
          initialHeight={650}
          initialWidth={1000}
        >
          <form className="k-form">
            <EntryPage />
          </form>
        </Window>
      )}

      <div className="align-columns">
        <Input
          className={templateData.value.templateName ? "!k-valid" : "!k-invalid"}
          style={{ width: "20%", marginTop: "10px" }}
          name={"txtTemplateName"}
          label={"Template Name"}
          required={true}
          value={templateData.value.templateName}
          defaultValue={templateData.value.templateName}
          onChange={(e) =>
            (templateData.value = { ...templateData.value, templateName: e.target.value })
          }
        />
        <SyncData
          id={templateData.value.id}
          templateProperties={templateData.value.properties}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column !important",
          justifyContent: "left",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <DropDownList
          className={templateData.value.company ? "!k-valid" : "!k-invalid"}
          style={{ width: "15%" }}
          name={"ddlTemplateCompany"}
          label={"Company"}
          required={true}
          data={companyDataset.value?.values?.map((row) => row.value)}
          value={templateData.value.company}
          onChange={(e) =>
            (templateData.value = { ...templateData.value, company: e.target.value })
          }
        />
        <DropDownList
          className={templateData.value.branch ? "!k-valid" : "!k-invalid"}
          style={{ width: "15%" }}
          name={"ddlTemplateBranch"}
          label={"Branch"}
          required={true}
          data={branchDataset.value?.values?.map((row) => row.value)}
          value={templateData.value.branch}
          onChange={(e) => (templateData.value = { ...templateData.value, branch: e.target.value })}
        />
        <DropDownList
          className={templateData.value.department ? "!k-valid" : "!k-invalid"}
          style={{ width: "15%" }}
          name={"ddlTemplateDepartment"}
          label={"Department"}
          required={true}
          data={departmentDataset.value?.values?.map((row) => row.value)}
          value={templateData.value.department}
          onChange={(e) =>
            (templateData.value = { ...templateData.value, department: e.target.value })
          }
        />
        <Input
          className={templateData.value.area ? "!k-valid" : "!k-invalid"}
          style={{ width: "15%" }}
          name={"txtTemplateArea"}
          label={"Area"}
          required={true}
          value={templateData.value.area}
          defaultValue={templateData.value.area}
          onChange={(e) => (templateData.value = { ...templateData.value, area: e.target.value })}
        />
      </div>
      <div className="k-my-4">
        <Button
          name="btnTemplateAddSection"
          type="button"
          themeColor={"primary"}
          onClick={() => addSection()}
        >
          Add Section
        </Button>
      </div>

      <SectionPanelBar />
    </div>
  );
};
